@if (!config) {
  <div class="container py-4 setup-container">
    <div class="card shadow-sm">
      <div class="card-header">
        <h4 class="mb-0">Kiosk Registration Setup</h4>
      </div>
      <div class="card-body">
        <form [formGroup]="setupForm" (ngSubmit)="saveSetup()">
          <div class="mb-3">
            <label for="propertyName" class="form-label">Property</label>
            <input
              id="propertyName"
              class="form-control"
              formControlName="propertyName"
              list="kioskPropertyList"
              placeholder="Select or enter property name"
              [class.is-invalid]="setupForm.get('propertyName')?.invalid && setupForm.get('propertyName')?.touched">
            <datalist id="kioskPropertyList">
              @for (property of properties; track property.id) {
                <option [value]="property.name"></option>
              }
            </datalist>
            <div class="invalid-feedback">Property name is required.</div>
          </div>

          <h6 class="mt-4">Registration Fields</h6>
          <table class="table table-sm align-middle" formGroupName="fields">
            <thead>
              <tr>
                <th>Field</th>
                <th class="text-center">Required</th>
                <th class="text-center">Optional</th>
                <th class="text-center">Off</th>
              </tr>
            </thead>
            <tbody>
              @for (field of fieldDefinitions; track field.key) {
                <tr>
                  <td>{{ field.label }}</td>
                  @for (mode of fieldModes; track mode) {
                    <td class="text-center">
                      <input
                        class="form-check-input"
                        type="radio"
                        [formControlName]="field.key"
                        [value]="mode"
                        [attr.aria-label]="field.label + ' ' + mode">
                    </td>
                  }
                </tr>
              }
            </tbody>
          </table>

          @if (!hasEnabledField) {
            <div class="text-danger mb-2">At least one field must be enabled.</div>
          }

          <button type="submit" class="btn btn-primary">Save &amp; Start Kiosk</button>
        </form>
      </div>
    </div>
  </div>
} @else {
  <div class="kiosk-shell">
    <div class="kiosk-screen">
      <div class="screen-head">
        <div class="status-widget">
          <div class="status-time">{{ currentTimeLabel }}</div>
          <div class="status-date">{{ currentDateLabel }}</div>
          <div class="status-weather">{{ weatherLabel }}</div>
        </div>
        <div class="estate-name">{{ config.propertyName }}</div>
        <button type="button" class="btn btn-sm settings-btn" aria-label="Reconfigure kiosk" (click)="reconfigure()">
          <fa-icon [icon]="faCog"></fa-icon>
        </button>
      </div>

      @if (registrationForm) {
        <form class="flow-card" [formGroup]="registrationForm" (ngSubmit)="submitRegistration()" novalidate>
          <h3 class="text-center">Visitor Registration</h3>
          <p class="flow-note text-center">Please fill in your details below.</p>

          @if (isEnabled('firstName')) {
            <div class="mb-3">
              <label for="firstName" class="form-label">First Name @if (isRequired('firstName')) {<span class="req">*</span>}</label>
              <input id="firstName" class="form-control kiosk-input" formControlName="firstName" autocomplete="off"
                [class.is-invalid]="showError('firstName')">
              <div class="invalid-feedback">Enter a valid first name.</div>
            </div>
          }

          @if (isEnabled('middleName')) {
            <div class="mb-3">
              <label for="middleName" class="form-label">Middle Name @if (isRequired('middleName')) {<span class="req">*</span>}</label>
              <input id="middleName" class="form-control kiosk-input" formControlName="middleName" autocomplete="off"
                [class.is-invalid]="showError('middleName')">
              <div class="invalid-feedback">Enter a valid middle name.</div>
            </div>
          }

          @if (isEnabled('lastName')) {
            <div class="mb-3">
              <label for="lastName" class="form-label">Last Name @if (isRequired('lastName')) {<span class="req">*</span>}</label>
              <input id="lastName" class="form-control kiosk-input" formControlName="lastName" autocomplete="off"
                [class.is-invalid]="showError('lastName')">
              <div class="invalid-feedback">Enter a valid last name.</div>
            </div>
          }

          @if (isEnabled('dob')) {
            <div class="mb-3">
              <label for="dob" class="form-label">Date of Birth @if (isRequired('dob')) {<span class="req">*</span>}</label>
              <input id="dob" type="date" class="form-control kiosk-input" formControlName="dob" [max]="maxDob"
                [class.is-invalid]="showError('dob')">
              <div class="invalid-feedback">Enter a valid date of birth.</div>
            </div>
          }

          @if (isEnabled('trn')) {
            <div class="mb-3">
              <label for="trn" class="form-label">T.R.N @if (isRequired('trn')) {<span class="req">*</span>}</label>
              <input id="trn" class="form-control kiosk-input" formControlName="trn" inputmode="numeric" maxlength="11"
                placeholder="000-000-000" autocomplete="off" [class.is-invalid]="showError('trn')">
              <div class="invalid-feedback">TRN must be 9 digits.</div>
            </div>
          }

          @if (isEnabled('registrantType')) {
            <div class="mb-3">
              <span class="form-label d-block">Company/Individual @if (isRequired('registrantType')) {<span class="req">*</span>}</span>
              <div class="type-toggle">
                <input type="radio" class="btn-check" id="typeIndividual" formControlName="registrantType" value="individual">
                <label class="btn btn-outline-light" for="typeIndividual">Individual</label>
                <input type="radio" class="btn-check" id="typeCompany" formControlName="registrantType" value="company">
                <label class="btn btn-outline-light" for="typeCompany">Company</label>
              </div>
              @if (showError('registrantType')) {
                <div class="text-danger small mt-1">Please select Company or Individual.</div>
              }
            </div>
          }

          @if (isEnabled('picture')) {
            <div class="mb-3">
              <span class="form-label d-block">Picture @if (isRequired('picture')) {<span class="req">*</span>}</span>

              @if (registrationForm.get('picture')?.value) {
                <img class="photo-preview" [src]="registrationForm.get('picture')?.value" alt="Captured photo">
                <button type="button" class="btn btn-outline-light mt-2 w-100" (click)="retakePhoto()">
                  <fa-icon class="me-2" [icon]="faRedo"></fa-icon>Retake Photo
                </button>
              } @else if (cameraActive) {
                <video #cameraVideo class="camera-preview" autoplay playsinline muted></video>
                <div class="flow-actions mt-2">
                  <button type="button" class="btn btn-outline-light" (click)="stopCamera()">Cancel</button>
                  <button type="button" class="btn btn-primary" (click)="capturePhoto()">
                    <fa-icon class="me-2" [icon]="faCamera"></fa-icon>Capture
                  </button>
                </div>
              } @else {
                <button type="button" class="btn btn-outline-light w-100 camera-btn" (click)="startCamera()">
                  <fa-icon class="me-2" [icon]="faCamera"></fa-icon>Take Photo
                </button>
              }

              @if (cameraError) {
                <div class="text-danger small mt-1">{{ cameraError }}</div>
              }
              @if (showError('picture')) {
                <div class="text-danger small mt-1">A photo is required.</div>
              }
            </div>
          }

          <div class="flow-actions mt-4">
            <button type="button" class="btn btn-outline-light" (click)="resetRegistration()">Clear</button>
            <button type="submit" class="btn btn-success">Submit</button>
          </div>
        </form>
      }

      @if (showSuccess) {
        <div class="success-overlay">
          <div class="success-modal">
            <fa-icon class="success-icon" [icon]="faCheckCircle"></fa-icon>
            <h4>Registration submitted</h4>
            <p class="mb-3">Thank you. Please wait for assistance.</p>
            <button type="button" class="btn btn-primary" (click)="resetRegistration()">Done</button>
          </div>
        </div>
      }
    </div>
  </div>
}
